import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CameraIcon, PhoneIcon } from '../../ui/icons';
import { useTranslation } from '../../translations';
import { cameraAvailability } from './cameraDecoder';
import CameraScanModal from './CameraScanModal';
import PhonePairing from './PhonePairing';

// Dwa przyciski obok miejsca, które przyjmuje kody: „Aparat" i „Telefon". Czytnik USB/Bluetooth
// nie potrzebuje przycisku (patrz utils/useBarcodeScan.js) — strona woła ten sam `onScan`.
//
// `onScan(kod)` może zwrócić `{ tone, text }` (także obietnicą) — trafia na podgląd aparatu
// i do paska telefonu, żeby osoba skanująca widziała skutek bez patrzenia na resztę ekranu.

const Row = styled.div`
  display: inline-flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const SourceButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${(p) => (p.$compact ? '7px 10px' : '9px 14px')};
  border-radius: var(--r-md);
  border: 1px solid var(--button-secondary-border, var(--input-border));
  background: var(--button-secondary-bg, var(--panel-bg));
  color: var(--button-secondary-text, var(--text));
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease);

  &:hover:not(:disabled) { border-color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// Telefon jako skaner komputera ma sens na KOMPUTERZE. Na telefonie (dotyk jako główne
// wejście) ten sam przycisk kazałby zeskanować QR samym sobą — zostaje tylko „Aparat".
function isTouchPrimary() {
  try { return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches; } catch (_) { return false; }
}

export default function ScanSources({ onScan, disabled = false, compact = false, cameraTitle, dataCyPrefix = 'scan' }) {
  const { t } = useTranslation();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const cameraSupported = useMemo(() => cameraAvailability() !== 'unsupported', []);
  const touch = useMemo(() => isTouchPrimary(), []);

  return (
    <>
      <Row>
        {cameraSupported && (
          <SourceButton
            type="button"
            $compact={compact}
            disabled={disabled}
            onClick={() => setCameraOpen(true)}
            title={t('scanner.cameraButtonHint')}
            data-cy={`${dataCyPrefix}-camera`}
          >
            <CameraIcon size={16} />
            {t('scanner.cameraButton')}
          </SourceButton>
        )}
        {!touch && (
          <SourceButton
            type="button"
            $compact={compact}
            disabled={disabled}
            onClick={() => setPhoneOpen(true)}
            title={t('scanner.phoneButtonHint')}
            data-cy={`${dataCyPrefix}-phone`}
          >
            <PhoneIcon size={16} />
            {t('scanner.phoneButton')}
          </SourceButton>
        )}
      </Row>
      <CameraScanModal open={cameraOpen} onClose={() => setCameraOpen(false)} onScan={onScan} title={cameraTitle} />
      <PhonePairing open={phoneOpen} onClose={() => setPhoneOpen(false)} onScan={onScan} />
    </>
  );
}
